// Get API URL with proper validation
const getApiUrl = () => {
  const url = import.meta.env.VITE_API_URL;
  if (!url || url === 'undefined' || url.trim() === '') {
    // In production, use api subdomain if on nativeit-botds.it
    if (import.meta.env.PROD) {
      const hostname = window.location.hostname;
      if (hostname === 'www.nativeit-botds.it' || hostname === 'nativeit-botds.it') {
        return 'https://api.nativeit-botds.it';
      }
      // IMPORTANT: on Vercel preview / vercel.app domains, current origin is the frontend.
      // Default to the backend domain to avoid serving index.html for /api/* requests.
      return 'https://api.nativeit-botds.it';
    }
    return 'http://localhost:3000';
  }

  if (import.meta.env.PROD) {
    try {
      const parsed = new URL(url);
      if (parsed.hostname === 'www.nativeit-botds.it' || parsed.hostname === 'nativeit-botds.it') {
        return 'https://api.nativeit-botds.it';
      }
    } catch {
      // Keep original URL if parsing fails
    }
  }

  return url;
};

const API_URL = getApiUrl();
const AUTH_TOKEN_KEY = 'auth_token';
const LEGACY_TOKEN_KEY = 'token';

export interface ApiError {
  error: string;
  message?: string;
  details?: any;
}

export interface User {
  id: string;
  discordId: string;
  username: string;
  discriminator: string | null;
  avatar: string | null;
  email: string | null;
  createdAt: string;
}

class ApiClient {
  private baseURL: string;
  private token: string | null = null;
  // Request deduplication: track ongoing requests
  private pendingRequests = new Map<string, Promise<any>>();
  // Circuit breaker: track consecutive failures
  private failureCount = 0;
  private readonly MAX_FAILURES = 3;
  private readonly CIRCUIT_BREAKER_RESET_TIME = 30000; // 30 seconds
  private circuitBreakerOpenUntil = 0;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    // Load token from localStorage on init (support legacy key)
    const authToken = localStorage.getItem(AUTH_TOKEN_KEY);
    const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
    this.token = authToken || legacyToken;

    // Auto-migrate legacy token to canonical key
    if (!authToken && legacyToken) {
      localStorage.setItem(AUTH_TOKEN_KEY, legacyToken);
    }
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      localStorage.setItem(LEGACY_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(LEGACY_TOKEN_KEY);
    }
  }

  getToken(): string | null {
    const currentToken = this.token || localStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(LEGACY_TOKEN_KEY);

    if (currentToken) {
      if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
        localStorage.setItem(AUTH_TOKEN_KEY, currentToken);
      }
      if (!localStorage.getItem(LEGACY_TOKEN_KEY)) {
        localStorage.setItem(LEGACY_TOKEN_KEY, currentToken);
      }
    }

    return currentToken;
  }

  private getRequestKey(endpoint: string, options: RequestInit): string {
    // Create a unique key for request deduplication
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    return `${method}:${endpoint}:${body}`;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    console.log('[API] Request:', endpoint, options.method || 'GET');
    // Check circuit breaker (TEMPORARILY DISABLED FOR DEBUGGING)
    const now = Date.now();
    // if (this.circuitBreakerOpenUntil > now) {
    //   const waitSeconds = Math.ceil((this.circuitBreakerOpenUntil - now) / 1000);
    //   const error = new Error(`Service temporarily unavailable. Please wait ${waitSeconds} second(s) and try again.`);
    //   (error as any).status = 503;
    //   (error as any).circuitBreaker = true;
    //   throw error;
    // }

    // Request deduplication: reuse ongoing requests
    const requestKey = this.getRequestKey(endpoint, options);
    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey)!;
    }

    const url = `${this.baseURL}${endpoint}`;
    const token = this.getToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    console.log('[API] Token present:', !!token, token ? `(${token.substring(0, 20)}...)` : 'none');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Create the request promise
    const requestPromise = (async (): Promise<T> => {
      try {
        const response = await fetch(url, {
          ...options,
          headers,
        });

        // Reset failure count on success
        this.failureCount = 0;
        this.circuitBreakerOpenUntil = 0;

        // Handle 401 Unauthorized - authentication required
        if (response.status === 401) {
          this.setToken(null);
          // Redirect to login or trigger auth refresh
          window.dispatchEvent(new CustomEvent('auth:logout'));
          throw new Error('Unauthorized - Please login again');
        }

        // Handle 403 Forbidden - invalid or expired token (same as 401 for auth)
        if (response.status === 403) {
          this.setToken(null);
          // Clear token and trigger logout
          window.dispatchEvent(new CustomEvent('auth:logout'));
          const errorData: ApiError = await response.json().catch(() => ({
            error: 'Invalid or expired token',
          }));
          const error = new Error(errorData.error || 'Invalid or expired token. Please login again.');
          (error as any).status = 403;
          throw error;
        }

        // Handle 429 Too Many Requests - rate limiting
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const waitMinutes = retryAfter ? Math.ceil(parseInt(retryAfter) / 60) : 15;
          const error = new Error(`Too many requests. Please wait ${waitMinutes} minute(s) and try again.`);
          (error as any).status = 429;
          throw error;
        }

        // Handle other errors
        if (!response.ok) {
          const errorData: ApiError = await response.json().catch(() => ({
            error: `HTTP ${response.status}: ${response.statusText}`,
          }));
          const error = new Error(errorData.error || errorData.message || 'Request failed');
          (error as any).details = errorData.details;
          (error as any).error_code = errorData.error_code;
          (error as any).status = response.status;
          throw error;
        }

        // Handle empty responses
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return await response.json();
        }

        // If the server returned 200 but not JSON, surface it.
        // This commonly happens when Vercel rewrites /api/* to /index.html (text/html).
        if (response.status === 204) {
          return {} as T;
        }
        const contentLength = response.headers.get('content-length');
        if (contentLength === '0') {
          return {} as T;
        }
        const text = await response.text().catch(() => '');
        if (!text || text.trim().length === 0) {
          return {} as T;
        }
        const snippet = text.slice(0, 200).replace(/\s+/g, ' ').trim();
        const err = new Error(
          `Unexpected non-JSON response (${contentType || 'no content-type'}) for ${endpoint}: ${snippet}`
        );
        (err as any).status = response.status;
        throw err;
      } catch (error) {
        // Handle network errors and resource exhaustion
        if (error instanceof TypeError) {
          const errorMessage = error.message.toLowerCase();
          
          // Check for resource exhaustion errors
          if (errorMessage.includes('failed to fetch') || 
              errorMessage.includes('insufficient resources') ||
              errorMessage.includes('networkerror')) {
            
            // Increment failure count
            this.failureCount++;
            
            // Open circuit breaker if too many failures
            if (this.failureCount >= this.MAX_FAILURES) {
              this.circuitBreakerOpenUntil = now + this.CIRCUIT_BREAKER_RESET_TIME;
              const waitSeconds = Math.ceil(this.CIRCUIT_BREAKER_RESET_TIME / 1000);
              const circuitError = new Error(
                `Too many connection failures. Please wait ${waitSeconds} seconds and refresh the page.`
              );
              (circuitError as any).status = 503;
              (circuitError as any).circuitBreaker = true;
              (circuitError as any).resourceExhausted = true;
              throw circuitError;
            }
            
            // Throw resource exhaustion error
            const resourceError = new Error(
              'Network resources exhausted. Please wait a moment and refresh the page.'
            );
            (resourceError as any).status = 503;
            (resourceError as any).resourceExhausted = true;
            throw resourceError;
          }
        }
        
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Network error - Please check your connection');
      } finally {
        // Remove from pending requests when done
        this.pendingRequests.delete(requestKey);
      }
    })();

    // Store the promise for deduplication
    this.pendingRequests.set(requestKey, requestPromise);
    
    return requestPromise;
  }

  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const api = new ApiClient(API_URL);

// Auth endpoints
export const authApi = {
  getMe: () => api.get<User>('/api/auth/me'),
  logout: () => api.post('/api/auth/logout'),
  getDiscordAuthUrl: async () => {
    // Get Discord OAuth URL from backend
    try {
      console.log('Fetching Discord OAuth URL from backend...');
      const response = await api.get<{ url: string }>('/api/auth/discord/login');
      console.log('Discord OAuth URL received:', response.url);
      if (!response.url) {
        throw new Error('Backend returned empty URL');
      }
      return response.url;
    } catch (error: any) {
      console.error('Failed to get Discord OAuth URL from backend:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        response: error.response,
      });
      
      // Fallback to client-side construction if backend is not available
      console.warn('Using fallback: constructing Discord OAuth URL client-side');
      const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
      if (!clientId) {
        const errorMsg = 'Discord Client ID not configured. Please set VITE_DISCORD_CLIENT_ID or ensure backend is accessible.';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
      
      const backendUrl = getApiUrl(); // Use the same function that determines API URL
      if (!backendUrl || backendUrl === 'undefined') {
        throw new Error('Backend URL is not configured. Please set VITE_API_URL environment variable.');
      }
      
      const redirectUri = encodeURIComponent(`${backendUrl}/api/auth/discord/callback`);
      const scopes = ['identify', 'guilds'];
      const authUrl = `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scopes.join('%20')}`;
      console.log('Fallback Discord OAuth URL:', authUrl);
      return authUrl;
    }
  },
};

// Server endpoints
export const serverApi = {
  getServers: () => api.get('/api/servers'),
  getServer: (id: string) => api.get(`/api/servers/${id}`),
  getBotInviteUrl: () => api.get<{ url: string }>('/api/servers/invite'),
  getBotStatus: (serverId: string) => api.get<{ botInServer: boolean; botMember: any }>(`/api/servers/${serverId}/bot-status`),
  updateServerSettings: (id: string, settings: any) =>
    api.put(`/api/servers/${id}/settings`, settings),
  getChannels: (id: string, type?: 'text' | 'voice') =>
    api.get<any[]>(`/api/servers/${id}/channels${type ? `?type=${type}` : ''}`),
  getCategories: (id: string) => api.get<any[]>(`/api/servers/${id}/categories`),
  getRoles: (id: string) => api.get<any[]>(`/api/servers/${id}/roles`),
};

// Plugin endpoints
export const pluginApi = {
  getPlugins: (serverId: string) => api.get(`/api/servers/${serverId}/plugins`),
  enablePlugin: (serverId: string, pluginId: string) =>
    api.put(`/api/servers/${serverId}/plugins/${pluginId}/enable`),
  disablePlugin: (serverId: string, pluginId: string) =>
    api.put(`/api/servers/${serverId}/plugins/${pluginId}/disable`),
  getPluginConfig: (serverId: string, pluginId: string) =>
    api.get(`/api/servers/${serverId}/plugins/${pluginId}/config`),
  updatePluginConfig: (serverId: string, pluginId: string, config: any) =>
    api.put(`/api/servers/${serverId}/plugins/${pluginId}/config`, { config }),
  getSocialAlerts: (serverId: string) =>
    api.get<Array<{ id: string; platform: string; username: string; channelId: string; enabled: boolean }>>(`/api/servers/${serverId}/plugins/social-alerts/alerts`),
  addSocialAlert: (serverId: string, payload: { platform: string; username: string; channelId?: string }) =>
    api.post(`/api/servers/${serverId}/plugins/social-alerts/alerts`, payload),
  removeSocialAlert: (serverId: string, alertId: string) =>
    api.delete(`/api/servers/${serverId}/plugins/social-alerts/alerts/${alertId}`),
  spawnWelcomeDesigner: (serverId: string) =>
    api.post(`/api/servers/${serverId}/plugins/welcome-designer/spawn`, {}),
  getSetupAssistantPreview: (serverId: string) =>
    api.get(`/api/servers/${serverId}/plugins/setup-assistant/preview`),
  applySetupAssistant: (serverId: string, dryRun = true, pluginIds?: string[]) =>
    api.post(`/api/servers/${serverId}/plugins/setup-assistant/apply`, {
      dryRun,
      ...(Array.isArray(pluginIds) && pluginIds.length > 0 ? { pluginIds } : {}),
    }),
};

export const bulkRolesApi = {
  getStatus: (serverId: string) =>
    api.get<{
      status: {
        running: boolean;
        inProgress: boolean;
        stopRequested?: boolean;
        startedAt: number | null;
        lastRunAt: number | null;
        nextRunAt: number | null;
        lastSummary: string;
      };
      config: {
        enabled: boolean;
        roleFromId: string;
        roleToId: string;
        intervalSeconds: number;
        maxUsersPerCycle: number;
        includeUserIds: string[];
        excludeUserIds: string[];
      };
    }>(`/api/servers/${serverId}/bulk-roles/status`),
  getLogs: (serverId: string, limit = 50) =>
    api.get<{ logs: Array<{ timestamp: string; level: 'info' | 'warn' | 'error'; message: string }> }>(
      `/api/servers/${serverId}/bulk-roles/logs?limit=${limit}`
    ),
  start: (
    serverId: string,
    payload: {
      roleFromId: string;
      roleToId: string;
      intervalSeconds: number;
      maxUsersPerCycle?: number;
      includeUserIds?: string[];
      excludeUserIds?: string[];
    }
  ) =>
    api.post(`/api/servers/${serverId}/bulk-roles/start`, payload),
  stop: (serverId: string) =>
    api.post(`/api/servers/${serverId}/bulk-roles/stop`, {}),
  runOnce: (serverId: string) =>
    api.post(`/api/servers/${serverId}/bulk-roles/run-once`, {}),
  assignAll: (
    serverId: string,
    payload: {
      roleId: string;
      batchSize?: number;
      includeBots?: boolean;
    }
  ) => api.post(`/api/servers/${serverId}/bulk-roles/assign-all`, payload),
};

// AI Helper endpoints
export const aiHelperApi = {
  getRules: (serverId: string) => api.get(`/api/servers/${serverId}/ai-helper/rules`),
  updateRules: (serverId: string, data: { title: string; content: string }) =>
    api.put(`/api/servers/${serverId}/ai-helper/rules`, data),
  getRegulations: (serverId: string) => api.get<Array<{
    id: string;
    serverId: string;
    title: string;
    content: string;
    channelId?: string | null;
    requiredRoleId?: string | null;
    aiEnabled?: boolean;
    lastUpdated: string;
    updatedBy?: string;
  }>>(`/api/servers/${serverId}/ai-helper/rules/list`),
  createRegulation: (serverId: string, data: { title: string; content: string; channelId?: string; requiredRoleId?: string; aiEnabled?: boolean }) =>
    api.post(`/api/servers/${serverId}/ai-helper/rules`, data),
  updateRegulation: (serverId: string, ruleId: string, data: { title: string; content: string; channelId?: string; requiredRoleId?: string; aiEnabled?: boolean }) =>
    api.put(`/api/servers/${serverId}/ai-helper/rules/${ruleId}`, data),
  deleteRegulation: (serverId: string, ruleId: string) =>
    api.delete(`/api/servers/${serverId}/ai-helper/rules/${ruleId}`),
  getStats: (serverId: string) => api.get(`/api/servers/${serverId}/ai-helper/stats`),
  ask: (serverId: string, question: string, ruleId?: string) =>
    api.post(`/api/servers/${serverId}/ai-helper/ask`, { question, ruleId }),
  spawnPanel: (serverId: string) =>
    api.post(`/api/servers/${serverId}/ai-helper/spawn`, {}),
  getPanelStatus: (serverId: string) =>
    api.get<{
      configured: boolean;
      helperChannelId: string;
      spawned: boolean;
      messageId: string | null;
      messageUrl: string | null;
    }>(`/api/servers/${serverId}/ai-helper/panel-status`),
  spawnRulesEmbed: (serverId: string, ruleId?: string) =>
    api.post(`/api/servers/${serverId}/ai-helper/spawn-rules`, { ruleId }),
  getRulesEmbedStatus: (serverId: string, ruleId?: string) =>
    api.get<{
      configured: boolean;
      rulesChannelId: string;
      spawned: boolean;
      messageId: string | null;
      messageUrl: string | null;
    }>(`/api/servers/${serverId}/ai-helper/rules-status${ruleId ? `?ruleId=${encodeURIComponent(ruleId)}` : ''}`),
};

// Tickets endpoints
export const ticketsApi = {
  getSettings: (serverId: string) => api.get(`/api/servers/${serverId}/tickets/settings`),
  updateSettings: async (serverId: string, settings: any) => {
    try {
      return await api.put(`/api/servers/${serverId}/tickets/settings`, settings);
    } catch (error: any) {
      if (error?.status === 405) {
        return api.post(`/api/servers/${serverId}/tickets/settings`, settings);
      }
      throw error;
    }
  },
  getStats: (serverId: string) => api.get(`/api/servers/${serverId}/tickets/stats`),
  getTickets: (serverId: string) => api.get(`/api/servers/${serverId}/tickets`),
  createTicket: (serverId: string, payload: any) =>
    api.post(`/api/servers/${serverId}/tickets`, payload),
  assignTicket: (serverId: string, ticketId: string, userId?: string) =>
    api.post(`/api/servers/${serverId}/tickets/${ticketId}/assign`, { userId }),
  closeTicket: (serverId: string, ticketId: string) =>
    api.post(`/api/servers/${serverId}/tickets/${ticketId}/close`),
  spawnPanel: (serverId: string, channelId?: string) =>
    api.post(`/api/servers/${serverId}/tickets/panel/spawn`, { channelId }),
};

export const verificationApi = {
  getPanelStatus: (serverId: string) =>
    api.get<{
      configured: boolean;
      verificationChannelId: string;
      spawned: boolean;
      messageId: string | null;
      messageUrl: string | null;
    }>(`/api/servers/${serverId}/verification/panel-status`),
  spawnPanel: (serverId: string, channelId?: string) =>
    api.post(`/api/servers/${serverId}/verification/panel/spawn`, { channelId }),
};

// Recording endpoints
export const recordingApi = {
  getRecordings: (serverId: string) => api.get(`/api/servers/${serverId}/recordings`),
  getTranscription: (recordingId: number) => api.get(`/api/recordings/${recordingId}/transcription`),
  deleteRecording: (recordingId: number) => api.delete(`/api/recordings/${recordingId}`),
};

// Leveling endpoints
export const levelingApi = {
  getLeaderboard: (serverId: string, limit = 10, offset = 0) =>
    api.get(`/api/servers/${serverId}/leveling/leaderboard?limit=${limit}&offset=${offset}`),
  getUserRank: (serverId: string, userId: string) =>
    api.get(`/api/servers/${serverId}/leveling/users/${userId}/rank`),
  updateXp: (serverId: string, userId: string, amount: number) =>
    api.put(`/api/servers/${serverId}/leveling/users/${userId}/xp`, { amount }),
  getStats: (serverId: string) =>
    api.get(`/api/servers/${serverId}/leveling/stats`),
  resetUser: (serverId: string, userId: string) =>
    api.delete(`/api/servers/${serverId}/leveling/users/${userId}`),
};

// Command endpoints
export const commandApi = {
  getCommands: (serverId: string) => api.get(`/api/servers/${serverId}/commands`),
  createCommand: (serverId: string, command: any) =>
    api.post(`/api/servers/${serverId}/commands`, command),
  updateCommand: (serverId: string, commandId: string, command: any) =>
    api.put(`/api/servers/${serverId}/commands/${commandId}`, command),
  deleteCommand: (serverId: string, commandId: string) =>
    api.delete(`/api/servers/${serverId}/commands/${commandId}`),
};

// Automation endpoints
export const automationApi = {
  getAutomations: (serverId: string) => api.get(`/api/servers/${serverId}/automations`),
  createAutomation: (serverId: string, automation: any) =>
    api.post(`/api/servers/${serverId}/automations`, automation),
  updateAutomation: (serverId: string, automationId: string, automation: any) =>
    api.put(`/api/servers/${serverId}/automations/${automationId}`, automation),
  deleteAutomation: (serverId: string, automationId: string) =>
    api.delete(`/api/servers/${serverId}/automations/${automationId}`),
  getAutomationLogs: (serverId: string, automationId: string) =>
    api.get(`/api/servers/${serverId}/automations/${automationId}/logs`),
  getBlueprintStructure: (serverId: string) =>
    api.get(`/api/servers/${serverId}/automations/blueprint/structure`),
  validateBlueprint: (serverId: string, blueprint: any) =>
    api.post(`/api/servers/${serverId}/automations/blueprint/validate`, { blueprint }),
  reviewBlueprint: (serverId: string, blueprint: any, goals?: string) =>
    api.post(`/api/servers/${serverId}/automations/blueprint/review`, { blueprint, goals }),
  applyBlueprint: (serverId: string, blueprint: any, dryRun?: boolean) =>
    api.post(`/api/servers/${serverId}/automations/blueprint/apply`, { blueprint, dryRun }),
};

export const aiServerArchitectApi = {
  getStructure: (serverId: string) =>
    api.get(`/api/servers/${serverId}/ai-server-architect/structure`),
  chat: (
    serverId: string,
    payload: {
      message: string;
      currentBlueprint?: any;
      goals?: string;
      allowDestructive?: boolean;
    }
  ) => api.post(`/api/servers/${serverId}/ai-server-architect/chat`, payload),
  apply: (
    serverId: string,
    payload: {
      blueprint: any;
      dryRun?: boolean;
      allowDestructive?: boolean;
    }
  ) => api.post(`/api/servers/${serverId}/ai-server-architect/apply`, payload),
  applyStream: async (
    serverId: string,
    payload: {
      blueprint: any;
      dryRun?: boolean;
      allowDestructive?: boolean;
    },
    onEvent: (event: { type: string; message: string; data?: any }) => void
  ) => {
    const token = api.getToken();
    const response = await fetch(`${API_URL}/api/servers/${serverId}/ai-server-architect/apply-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok || !response.body) {
      const errorText = await response.text().catch(() => 'Streaming request failed');
      throw new Error(errorText || 'Streaming request failed');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          const parsed = JSON.parse(trimmed);
          onEvent(parsed);
        } catch {
          // ignore malformed lines
        }
      }
    }

    if (buffer.trim()) {
      try {
        onEvent(JSON.parse(buffer.trim()));
      } catch {
        // ignore final malformed chunk
      }
    }
  },
};

export const permissionsManagerApi = {
  getTree: (serverId: string) => api.get(`/api/servers/${serverId}/permissions/tree`),
  updateChannelOverwrites: (
    serverId: string,
    channelId: string,
    payload: { roleId: string; changes: Record<string, 'ALLOW' | 'DENY' | 'INHERIT'> }
  ) => api.patch(`/api/servers/${serverId}/permissions/channels/${channelId}/overwrites`, payload),
  moveChannel: (
    serverId: string,
    channelId: string,
    payload: { categoryId: string | null }
  ) => api.patch(`/api/servers/${serverId}/permissions/channels/${channelId}/parent`, payload),
  troubleshoot: (
    serverId: string,
    payload: { message: string; username?: string; channelName?: string }
  ) => api.post(`/api/servers/${serverId}/permissions/troubleshoot`, payload),
  applyFix: (serverId: string, payload: { fix: any; context?: any }) =>
    api.post(`/api/servers/${serverId}/permissions/apply-fix`, payload),
};

// Reaction Role endpoints
export const reactionRoleApi = {
  getReactionRoles: (serverId: string) => api.get(`/api/servers/${serverId}/reaction-roles`),
  createReactionRole: (serverId: string, reactionRole: any) =>
    api.post(`/api/servers/${serverId}/reaction-roles`, reactionRole),
  updateReactionRole: (serverId: string, reactionRoleId: string, reactionRole: any) =>
    api.put(`/api/servers/${serverId}/reaction-roles/${reactionRoleId}`, reactionRole),
  deleteReactionRole: (serverId: string, reactionRoleId: string) =>
    api.delete(`/api/servers/${serverId}/reaction-roles/${reactionRoleId}`),
};

// Social Alert endpoints
export const socialAlertApi = {
  getSocialAlerts: (serverId: string) => api.get(`/api/servers/${serverId}/social-alerts`),
  createSocialAlert: (serverId: string, alert: any) =>
    api.post(`/api/servers/${serverId}/social-alerts`, alert),
  deleteSocialAlert: (serverId: string, alertId: string) =>
    api.delete(`/api/servers/${serverId}/social-alerts/${alertId}`),
};

// Giveaway endpoints
export const giveawayApi = {
  getGiveaways: (serverId: string) => api.get(`/api/servers/${serverId}/giveaways`),
  createGiveaway: (serverId: string, giveaway: any) =>
    api.post(`/api/servers/${serverId}/giveaways`, giveaway),
  updateGiveaway: (serverId: string, giveawayId: string, giveaway: any) =>
    api.put(`/api/servers/${serverId}/giveaways/${giveawayId}`, giveaway),
  deleteGiveaway: (serverId: string, giveawayId: string) =>
    api.delete(`/api/servers/${serverId}/giveaways/${giveawayId}`),
  endGiveaway: (serverId: string, giveawayId: string) =>
    api.post(`/api/servers/${serverId}/giveaways/${giveawayId}/end`),
};

// Moderation endpoints
export const moderationApi = {
  getWarnings: (serverId: string) => api.get(`/api/servers/${serverId}/moderation/warnings`),
  getUserWarnings: (serverId: string, userId: string) =>
    api.get(`/api/servers/${serverId}/moderation/warnings/user/${userId}`),
  createWarning: (serverId: string, data: { userId: string; reason: string }) =>
    api.post(`/api/servers/${serverId}/moderation/warnings`, data),
  deleteWarning: (serverId: string, warningId: string) =>
    api.delete(`/api/servers/${serverId}/moderation/warnings/${warningId}`),
  getStats: (serverId: string) =>
    api.get(`/api/servers/${serverId}/moderation/stats`),
};

// Notification endpoints
export const notificationApi = {
  getNotifications: () => api.get('/api/notifications'),
  getUnreadCount: () => api.get<{ count: number }>('/api/notifications/unread-count'),
  markAsRead: (notificationId: string) =>
    api.put(`/api/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/api/notifications/mark-all-read'),
  deleteNotification: (notificationId: string) =>
    api.delete(`/api/notifications/${notificationId}`),
};

// AutoMod endpoints
export const automodApi = {
  getRules: (serverId: string) => api.get(`/api/servers/${serverId}/automod/rules`),
  getRule: (serverId: string, ruleId: string) => api.get(`/api/servers/${serverId}/automod/rules/${ruleId}`),
  createRule: (serverId: string, rule: any) => api.post(`/api/servers/${serverId}/automod/rules`, rule),
  updateRule: (serverId: string, ruleId: string, updates: any) => api.put(`/api/servers/${serverId}/automod/rules/${ruleId}`, updates),
  deleteRule: (serverId: string, ruleId: string) => api.delete(`/api/servers/${serverId}/automod/rules/${ruleId}`),
  getViolations: (serverId: string, limit = 50, offset = 0) => api.get(`/api/servers/${serverId}/automod/violations?limit=${limit}&offset=${offset}`),
  getStats: (serverId: string) => api.get(`/api/servers/${serverId}/automod/stats`),
  getScamHashes: (serverId: string) => api.get(`/api/servers/${serverId}/automod/scam-hashes`),
  addScamHash: (serverId: string, payload: { phash?: string; label?: string; imageUrl?: string; global?: boolean }) =>
    api.post(`/api/servers/${serverId}/automod/scam-hashes`, payload),
  deleteScamHash: (serverId: string, hashId: string) =>
    api.delete(`/api/servers/${serverId}/automod/scam-hashes/${hashId}`),
};

// Logging endpoints
export const loggingApi = {
  createWebhook: (serverId: string, channelId: string, name?: string) =>
    api.post(`/api/servers/${serverId}/logging/webhook`, { channelId, name }),
  getEntries: (
    serverId: string,
    limit = 20,
    type?: string,
    action?: string,
    search?: string
  ) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (type) params.set('type', type);
    if (action) params.set('action', action);
    if (search) params.set('search', search);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/api/servers/${serverId}/logging/entries${suffix}`);
  },
  getAnalysis: (serverId: string, hours = 24) =>
    api.get(`/api/servers/${serverId}/logging/entries/analysis?hours=${hours}`),
};

// Diagnostics endpoints
export const diagnosticsApi = {
  getLevelingDiagnostics: (serverId: string) => 
    api.get(`/api/servers/${serverId}/diagnostics/leveling`),
  forceEnableLeveling: (serverId: string) => 
    api.post(`/api/servers/${serverId}/diagnostics/leveling/force-enable`, {}),
  testXp: (serverId: string, userId: string, amount: number) => 
    api.post(`/api/servers/${serverId}/diagnostics/leveling/test-xp`, { userId, amount }),
};
